<script>
  import { debounce, isArray, isEqual, uniqBy } from "lodash";
  import { onMount } from "svelte";
  import htmx from "htmx.org";

  export let categoriesOne;
  export let categoriesTwo;
  let categoryTwoSearchText = "";
  let collapse = true;
  let keywordSearchText = "";
  let triggerSearch = "";

  onMount(() => {
    categoriesOne = JSON.parse(categoriesOne ?? "[]").map((c) => ({
      ...c,
      applied: false,
    }));
    categoriesTwo = JSON.parse(categoriesTwo ?? "[]").map((c) => ({
      ...c,
      applied: false,
    }));
  });

  const debouncedTypeaheadSearch = debounce(function (search, setOptions, url) {
    fetch(`${url}${search}`)
      .then((response) => response.json())
      .then((data) => {
        const options = data.map((item) => ({
          id: item.id,
          text: item.text,
          value: item.text,
          applied: false,
        }));
        setOptions(options);
      })
      .catch((error) => {
        console.log("Failed to fetch data", error);
      });
  }, 300);

  let locationOptions = [];
  let locationSearch = "";
  let advertiserSearch = "";
  let advertiserOptions = [];

  function searchLocations() {
    const currentSearch = locationSearch;
    debouncedTypeaheadSearch(
      locationSearch,
      (options) => {
        if (locationSearch === currentSearch) {
          locationOptions = uniqBy(
            [...locationOptions.filter((o) => o.applied), ...options],
            "id"
          );
        }
      },
      "/ajax/findAClassifiedAd?locationString="
    );
  }

  function searchAdvertisers() {
    const currentSearch = advertiserSearch;
    debouncedTypeaheadSearch(
      advertiserSearch,
      (options) => {
        if (advertiserSearch === currentSearch) {
          advertiserOptions = uniqBy(
            [...advertiserOptions.filter((o) => o.applied), ...options],
            "id"
          );
        }
      },
      "/ajax/findAClassifiedAd?nameString="
    );
  }

  $: if (locationSearch.length > 0) {
    searchLocations();
  }

  $: if (advertiserSearch.length > 0) {
    searchAdvertisers();
  }

  function performSearch(
    categoryIds,
    subCategoryIds,
    locations,
    advertisers,
    triggerSearch
  ) {
    const element = htmx.closest(
      htmx.find(`#adResults`),
      'div[data-hx-trigger="refresh"]'
    );
    if (element) {
      htmx.ajax("GET", element.dataset.hxGet, {
        target: `#adResults`,
        values: {
          ...JSON.parse(element.dataset.hxVals || "{}"),
          categoryIds: categoryIds.join(','),
          subCategoryIds: subCategoryIds.join(','),
          searchText: triggerSearch,
          locations: locations.join(','),
          advertisers: advertisers.join(','),
        },
      });
    }
  }

  // Variables to store previous values
  let prevCategoriesOne = [];
  let prevCategoriesTwo = [];
  let prevLocationOptions = [];
  let prevAdvertiserOptions = [];
  let prevTriggerSearch = false;

  $: {
    if (
      isArray(categoriesOne) &&
      isArray(categoriesTwo) &&
      isArray(locationOptions) &&
      isArray(advertiserOptions)
    ) {
      const currentCategoriesOne = categoriesOne
        .filter((c) => c.applied)
        .map((c) => c.id);
      const currentCategoriesTwo = categoriesTwo
        .filter((c) => c.applied)
        .map((c) => c.id);
      const currentLocationOptions = locationOptions
        .filter((c) => c.applied)
        .map((c) => c.value );
      const currentAdvertiserOptions = advertiserOptions
        .filter((c) => c.applied)
        .map((c) => c.value);
      const currentTriggerSearch = triggerSearch;
      // Check if current values are different from previous values
      if (
        !isEqual(prevCategoriesOne, currentCategoriesOne) ||
        !isEqual(prevCategoriesTwo, currentCategoriesTwo) ||
        !isEqual(prevLocationOptions, currentLocationOptions) ||
        !isEqual(prevAdvertiserOptions, currentAdvertiserOptions) ||
        prevTriggerSearch !== currentTriggerSearch
      ) {
        // Update previous values
        prevCategoriesOne = currentCategoriesOne;
        prevCategoriesTwo = currentCategoriesTwo;
        prevLocationOptions = currentLocationOptions;
        prevAdvertiserOptions = currentAdvertiserOptions;
        prevTriggerSearch = currentTriggerSearch;

        // Call performSearch with current values
        performSearch(
          currentCategoriesOne,
          currentCategoriesTwo,
          currentLocationOptions,
          currentAdvertiserOptions,
          currentTriggerSearch
        );
      }
    }
  }
</script>

{#if isArray(categoriesOne) && isArray(categoriesTwo)}
  <section class="filter-section">
    <div class="filter-form many-filters" id="reefine_form">
      <button
        type="button"
        class="btn btn-outline-dark btn-mobile btn-filter d-md-none"
        data-bs-toggle="collapse"
        data-bs-target=".navbar-center"
        on:click|preventDefault={() => {
          collapse = !collapse;
        }}>FILTER<span class="icon-dropdown">&nbsp;</span></button
      >

      <div class="filter-select-container">
        <ul
          class="navbar-nav navbar-center navbar-collapse {collapse
            ? 'collapse'
            : ''} "
        >
          <li
            class="nav-item dropdown {categoriesOne?.some?.((c) => c.applied)
              ? 'filters-applied'
              : ''}"
          >
            <a
              href="#0"
              class="dropdown-toggle"
              data-bs-toggle="dropdown"
              data-bs-target=".dropdown-menu"
              aria-label="Dental & Dental Specialists"
              aria-haspopup="true">Dental & Dental Specialists</a
            >
            <button
              class="mobile-dropdown-toggle dropdown-toggle"
              aria-haspopup="true"
            />

            <ul class="dropdown-menu" style="width: max-content">
              {#each categoriesOne as category}
                <li class="dropdown-item form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    bind:checked={category.applied}
                    id={category.id}
                  />
                  <label class="form-check-label" for={category.id}
                    >{category.title}</label
                  >
                </li>
              {/each}
            </ul>
          </li>

          <li
            class="nav-item dropdown {categoriesTwo?.some?.((c) => c.applied)
              ? 'filters-applied'
              : ''}"
          >
            <a
              href="#0"
              class="dropdown-toggle"
              data-bs-toggle="dropdown"
              data-bs-target=".dropdown-menu"
              aria-label="Dental Team"
              aria-haspopup="true">Dental Team</a
            >
            <button
              class="mobile-dropdown-toggle dropdown-toggle"
              aria-haspopup="true"
            />
            <div class="dropdown-menu" style="width: max-content" aria-expanded="false" aria-hidden="true">
              <div class="form-group option-filter">
                <input
                  type="text"
                  class="form-control"
                  id="subcategory-search"
                  bind:value={categoryTwoSearchText}
                  placeholder="Search"
                />
                <label for="subcategory-search">Search</label>
              </div>

              <ul>
                {#each categoryTwoSearchText.length == 0 ? categoriesTwo : categoriesTwo?.filter?.( (c) => c.title
                          .toLowerCase()
                          .includes(categoryTwoSearchText.toLowerCase()) ) ?? [] as category}
                  <li class="dropdown-item form-check">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      bind:checked={category.applied}
                      id={category.id}
                    />
                    <label class="form-check-label" for={category.id}
                      >{category.title}</label
                    >
                  </li>
                {/each}
              </ul>
            </div>
          </li>

          <li
            class="nav-item dropdown {locationOptions?.some?.((c) => c.applied)
              ? 'filters-applied'
              : ''}"
          >
            <a
              href="#0"
              class="dropdown-toggle"
              data-bs-toggle="dropdown"
              data-bs-target=".dropdown-menu"
              aria-label="Location"
              aria-haspopup="true">Location</a
            >

            <button
              class="mobile-dropdown-toggle dropdown-toggle"
              aria-haspopup="true"
            />
            <div class="dropdown-menu" style="width: max-content" aria-expanded="false" aria-hidden="true">
              <div class="form-group option-filter">
                <input
                  type="text"
                  class="form-control"
                  id="location-search"
                  bind:value={locationSearch}
                  placeholder="Search"
                />
                <label for="location-search">Search</label>
              </div>
              <ul>
                {#each locationOptions ?? [] as location}
                  <li class="dropdown-item form-check">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      bind:checked={location.applied}
                      id={location.value}
                    />
                    <label class="form-check-label" for={location.value}
                      >{location.text}</label
                    >
                  </li>
                {/each}
              </ul>
            </div>
          </li>

          <!-- <li
            class="nav-item dropdown {advertiserOptions?.some?.(
              (c) => c.applied
            )
              ? 'filters-applied'
              : ''}"
          >
            <a
              href="#0"
              class="dropdown-toggle"
              data-bs-toggle="dropdown"
              data-bs-target=".dropdown-menu"
              aria-label="Advertiser"
              aria-haspopup="true">Advertiser</a
            >
            <button
              class="mobile-dropdown-toggle dropdown-toggle"
              aria-haspopup="true"
            />
            <div class="dropdown-menu" style="width: max-content" aria-expanded="false" aria-hidden="true">
              <div class="form-group option-filter">
                <input
                  type="text"
                  class="form-control"
                  id="advertiser-search"
                  bind:value={advertiserSearch}
                  placeholder="Search"
                />
                <label for="advertiser-search">Search</label>
              </div>
              <ul>
                {#each advertiserOptions ?? [] as advertiser}
                  <li class="dropdown-item form-check">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      bind:checked={advertiser.applied}
                      id={advertiser.value}
                    />
                    <label class="form-check-label" for={advertiser.value}
                      >{advertiser.text}</label
                    >
                  </li>
                {/each}
              </ul>
            </div>
          </li> -->
        </ul>
      </div>

      {#if [...categoriesOne, ...categoriesTwo, ...locationOptions, ...advertiserOptions].some((c) => c.applied)}
        <div class="applied-filters">
          <ul>
            <li class="filter heading">Filters applied:</li>
            {#each categoriesOne.filter((c) => c.applied) as category}
              <li class="filter">
                <span class="filter-text">{category.title}</span><a
                  on:click={() => {
                    category.applied = false;
                    categoriesOne = categoriesOne;
                  }}
                  class="filter-delete"
                />
              </li>
            {/each}
            {#each categoriesTwo.filter((c) => c.applied) as category}
              <li class="filter">
                <span class="filter-text">{category.title}</span><a
                  on:click={() => {
                    category.applied = false;
                    categoriesTwo = categoriesTwo;
                  }}
                  class="filter-delete"
                />
              </li>
            {/each}
            {#each locationOptions.filter((c) => c.applied) as option}
              <li class="filter">
                <span class="filter-text">{option.text}</span><a
                  on:click={() => {
                    option.applied = false;
                    locationOptions = locationOptions;
                  }}
                  class="filter-delete"
                />
              </li>
            {/each}
            {#each advertiserOptions.filter((c) => c.applied) as option}
              <li class="filter">
                <span class="filter-text">{option.text}</span><a
                  on:click={() => {
                    option.applied = false;
                    advertiserOptions = advertiserOptions;
                  }}
                  class="filter-delete"
                />
              </li>
            {/each}
            <li class="filter clear-filters">
              <a
                href
                class="btn btn-clear"
                title="reset"
                on:click={() => {
                  categoriesOne.forEach((c) => (c.applied = false));
                  categoriesOne = categoriesOne;
                  categoriesTwo.forEach((c) => (c.applied = false));
                  categoriesTwo = categoriesTwo;
                  locationOptions.forEach((c) => (c.applied = false));
                  locationOptions = locationOptions;
                  advertiserOptions.forEach((c) => (c.applied = false));
                  advertiserOptions = advertiserOptions;
                }}>Remove all<span class="icon-clear">&nbsp;</span></a
              >
            </li>
          </ul>
        </div>
      {/if}

      <form
        on:submit|preventDefault={() => {
          triggerSearch = keywordSearchText;
        }}
        class="input-group d-flex align-items-center search-group"
      >
        <input
          type="text"
          name="title"
          placeholder="Keyword search"
          class="form-control w100"
          bind:value={keywordSearchText}
          disabled={triggerSearch.length > 0}
          title="Search"
        />
        <div class="input-group-btn">
          {#if triggerSearch.length > 0}
          <button type="reset" on:click|preventDefault={()=>{
            triggerSearch = ''
            keywordSearchText = '';
          }} class="btn btn-clear not-freeform"
          ><span class="icon-clear">&nbsp;</span></button
        >
          {:else}
          <button type="submit" class="btn btn-primary btn-search not-freeform"
          ><span class="icon-search">&nbsp;</span></button
        >
          {/if}
          
        </div>
      </form>
    </div>
  </section>
{/if}
